import { Col, Form, InputNumber, Modal, Row, Select } from "antd";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";

import lang from "../../../helper/langHelper";
import { Severty, ShowToast } from "../../../helper/toast";
import useRequest from "../../../hooks/useRequest";

const normalizeOptions = (items = []) =>
  (Array.isArray(items) ? items : [])
    .map((item) => ({
      value: item?._id || item?.id || item?.value || item,
      label: item?.label || item?.name || item?.title || item?.slug || item?.value || item?._id || item,
      raw: item,
    }))
    .filter((item) => item.value);

const normalizeId = (value) => value?._id || value?.id || value?.value || value || "";

const AddForm = ({ api, show, hide, data, refresh, productOptions = [], variantOptions = [] }) => {
  const [form] = Form.useForm();
  const { request } = useRequest();
  const [loading, setLoading] = useState(false);
  const [selectedProductId, setSelectedProductId] = useState("");
  const [productVariantOptions, setProductVariantOptions] = useState([]);
  const [variantLoading, setVariantLoading] = useState(false);
  const latestVariantRequestRef = useRef("");

  const normalizedVariantOptions = useMemo(() => normalizeOptions(variantOptions), [variantOptions]);

  const fetchVariantsByProduct = useCallback((productId) => {
    const normalizedProductId = String(productId || "");

    if (!normalizedProductId) {
      latestVariantRequestRef.current = "";
      setProductVariantOptions([]);
      setVariantLoading(false);
      return;
    }

    latestVariantRequestRef.current = normalizedProductId;
    setVariantLoading(true);
    request({
      url: `${api.variants}?page=1&pageSize=100&productId=${normalizedProductId}`,
      method: "GET",
      onSuccess: ({ data }) => {
        if (latestVariantRequestRef.current !== normalizedProductId) {
          return;
        }

        setVariantLoading(false);
        setProductVariantOptions(normalizeOptions(data?.docs || data));
      },
      onError: (error) => {
        if (latestVariantRequestRef.current !== normalizedProductId) {
          return;
        }

        setVariantLoading(false);
        setProductVariantOptions([]);
        ShowToast(error?.response?.data?.message || error?.message || "Failed to load variants", Severty.ERROR);
      },
    });
  }, [api.variants, request]);

  const filteredVariants = useMemo(() => {
    const sourceOptions = productVariantOptions.length ? productVariantOptions : normalizedVariantOptions;

    if (!selectedProductId) {
      return sourceOptions;
    }

    return sourceOptions.filter((item) => {
      const productId = normalizeId(item.raw?.product_id);
      return String(productId) === String(selectedProductId);
    });
  }, [normalizedVariantOptions, productVariantOptions, selectedProductId]);

  useEffect(() => {
    if (!data) {
      form.resetFields();
      setSelectedProductId("");
      setProductVariantOptions([]);
      return;
    }

    const productId = normalizeId(data?.product_id);
    const variantId = normalizeId(data?.variant_id);

    form.setFieldsValue({
      ...data,
      product_id: productId,
      variant_id: variantId,
      movement_type: data?.movement_type || null,
      source: data?.source || null,
      quantity: data?.quantity ?? null,
    });
    setSelectedProductId(productId);
  }, [data, form]);

  useEffect(() => {
    if (!selectedProductId) {
      latestVariantRequestRef.current = "";
      setProductVariantOptions([]);
      setVariantLoading(false);
      return;
    }

    fetchVariantsByProduct(selectedProductId);
  }, [fetchVariantsByProduct, selectedProductId]);

  const handleProductChange = (value) => {
    setSelectedProductId(value || "");
    form.setFieldsValue({ variant_id: undefined });
  };

  const onCreate = (values) => {
    setLoading(true);

    const payload = {
      product_id: values.product_id || "",
      variant_id: values.variant_id || "",
      movement_type: values.movement_type || "",
      source: values.source || "",
      quantity: values.quantity ?? "",
    };

    request({
      url: `${data ? `${api.addEdit}/${data._id}` : api.addEdit}`,
      method: data ? "PUT" : "POST",
      data: payload,
      onSuccess: (response) => {
        setLoading(false);
        if (response.status) {
          ShowToast(response.message, Severty.SUCCESS);
          hide();
          refresh();
        } else {
          ShowToast(response.message, Severty.ERROR);
        }
      },
      onError: (error) => {
        setLoading(false);
        ShowToast(error?.response?.data?.message || error?.message || "Something went wrong", Severty.ERROR);
      },
    });
  };

  return (
    <Modal
      open={show}
      width={900}
      okText={data ? lang("Update") : lang("Add")}
      cancelText={lang("Cancel")}
      onCancel={hide}
      okButtonProps={{
        form: "stock-tracking-form",
        htmlType: "submit",
        loading,
      }}
      centered
      className="tab_modal"
    >
      <Form
        id="stock-tracking-form"
        form={form}
        onFinish={onCreate}
        layout="vertical"
        initialValues={{
          product_id: null,
          variant_id: null,
          movement_type: null,
          source: null,
          quantity: null,
        }}
      >
        <h4 className="modal_title_cls">{data ? lang("Edit Stock Entry") : lang("Add Stock Entry")}</h4>
        <Row gutter={[16, 0]}>
          <Col span={24} md={12}>
            <Form.Item label={lang("Product")} name="product_id" rules={[{ required: true, message: lang("Please select product!") }]}>
              <Select allowClear showSearch placeholder={lang("Select Product")} optionFilterProp="label" onChange={handleProductChange} getPopupContainer={(triggerNode) => triggerNode.parentNode}>
                {normalizeOptions(productOptions).map((item) => (
                  <Select.Option key={item.value} value={item.value} label={item.label}>
                    {item.label}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </Col>

          <Col span={24} md={12}>
            <Form.Item label={lang("Variant")} name="variant_id" rules={[{ required: true, message: lang("Please select variant!") }]}>
              <Select
                allowClear
                showSearch
                placeholder={lang("Select Variant")}
                optionFilterProp="label"
                disabled={!selectedProductId}
                loading={variantLoading}
                getPopupContainer={(triggerNode) => triggerNode.parentNode}
              >
                {filteredVariants.map((item) => (
                  <Select.Option key={item.value} value={item.value} label={item.label}>
                    {item.label}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </Col>

          <Col span={24} md={12}>
            <Form.Item label={lang("Movement Type")} name="movement_type" rules={[{ required: true, message: lang("Please select movement type!") }]}>
              <Select allowClear placeholder={lang("Select Movement Type")} getPopupContainer={(triggerNode) => triggerNode.parentNode}>
                <Select.Option value="stock_in">{lang("Stock In")}</Select.Option>
                <Select.Option value="stock_out">{lang("Stock Out")}</Select.Option>
              </Select>
            </Form.Item>
          </Col>

          <Col span={24} md={12}>
            <Form.Item label={lang("Source")} name="source">
              <Select allowClear placeholder={lang("Select Source")} getPopupContainer={(triggerNode) => triggerNode.parentNode}>
                <Select.Option value="purchase">{lang("Purchase")}</Select.Option>
                <Select.Option value="order">{lang("Order")}</Select.Option>
                <Select.Option value="return">{lang("Return")}</Select.Option>
              </Select>
            </Form.Item>
          </Col>

          <Col span={24} md={12}>
            <Form.Item label={lang("Quantity")} name="quantity" rules={[{ required: true, message: lang("Quantity is required!") }]}>
              <InputNumber min={1} style={{ width: "100%" }} placeholder={lang("Enter Quantity")} />
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </Modal>
  );
};

export default AddForm;
